import { readFileSync, writeFileSync, existsSync, mkdirSync } from 'fs'
import { dirname } from 'path'
import { parse as parseYaml, stringify as stringifyYaml } from 'yaml'
import {
  type ArchitectureEnvelope,
  type ModuleBoundary,
  type ChangeLevel,
  type EnvelopeValidation,
} from './index.js'

export interface DeclaredInterface {
  module: string
  source_glob: string
  exports: string[]
}

export interface ArchitectureBaseline {
  version: string
  frozen_at: string
  /**
   * Git revision at freeze time (full 40-char SHA). Written by step-8 freeze
   * via `git rev-parse HEAD`. Reconcile (step-7) uses this as the interface-diff
   * baseline to compute per-file added/removed/changed exports. See ADR-0022.
   * Optional for backward compatibility — when absent, reconcile falls back
   * to `git rev-list --before=<frozen_at> -1`.
   */
  frozen_at_sha?: string
  frozen_by_req: string
  legal_layer?: ArchitectureEnvelope['legal_layer']
  module_boundaries: {
    writable: ModuleBoundary[]
    read_only: ModuleBoundary[]
    forbidden: ModuleBoundary[]
  }
  declared_interfaces: DeclaredInterface[]
  absorption_budget: { max_parallel_slices: number }
}

export type UpgradeFlag = 'none' | 'L2-escape' | 'L3-rewrite' | 'baseline-breaking'

export interface GateChange {
  gate_id: string
  change: string
  reason: string
}

export interface ArchitectureDelta {
  request_id: string
  based_on_baseline: string
  delta: {
    legal_layer?: ArchitectureEnvelope['legal_layer']
    module_boundaries?: {
      writable?: ModuleBoundary[]
      read_only?: ModuleBoundary[]
      forbidden?: ModuleBoundary[]
    }
    declared_interfaces?: DeclaredInterface[]
    absorption_budget?: { max_parallel_slices: number }
  }
  理由: string
  冲击不变量: string[]
  gate_变更: GateChange[]
  升级标记: UpgradeFlag
}

export function loadBaseline(path: string): ArchitectureBaseline {
  if (!existsSync(path)) {
    throw new Error(`Baseline file not found: ${path}`)
  }
  const raw = readFileSync(path, 'utf-8')
  const parsed = parseYaml(raw)
  return normalizeBaseline(parsed)
}

export function writeBaseline(path: string, baseline: ArchitectureBaseline): void {
  const dir = dirname(path)
  if (!existsSync(dir)) {
    mkdirSync(dir, { recursive: true })
  }
  const yaml = stringifyYaml(baseline, { sortMapEntries: false })
  writeFileSync(path, yaml, 'utf-8')
}

export function loadDelta(path: string): ArchitectureDelta {
  if (!existsSync(path)) {
    throw new Error(`Delta file not found: ${path}`)
  }
  const raw = readFileSync(path, 'utf-8')
  const parsed = parseYaml(raw)
  return normalizeDelta(parsed)
}

export function writeDelta(path: string, delta: ArchitectureDelta): void {
  const dir = dirname(path)
  if (!existsSync(dir)) {
    mkdirSync(dir, { recursive: true })
  }
  const yaml = stringifyYaml(delta, { sortMapEntries: false })
  writeFileSync(path, yaml, 'utf-8')
}

const UPGRADE_FLAGS: readonly UpgradeFlag[] = ['none', 'L2-escape', 'L3-rewrite', 'baseline-breaking']

export function validateBaseline(baseline: ArchitectureBaseline): EnvelopeValidation {
  const errors: string[] = []
  const { writable, read_only, forbidden } = baseline.module_boundaries

  if (writable.length === 0 && read_only.length === 0 && forbidden.length === 0) {
    errors.push('module_boundaries has no entries — at least one writable path is required')
  }
  if (writable.length === 0) {
    errors.push('module_boundaries.writable is empty — deny-by-default requires at least one allowed path')
  }
  if (!baseline.declared_interfaces) {
    errors.push('declared_interfaces is missing — graphify diff requires declared interfaces')
  } else if (!Array.isArray(baseline.declared_interfaces)) {
    errors.push('declared_interfaces must be an array')
  }
  if (!baseline.frozen_by_req) {
    errors.push('frozen_by_req is required (use the originating REQ-ID; empty string only for 0→1 baseline)')
  }
  if (!baseline.version) {
    errors.push('version is required')
  }

  return { valid: errors.length === 0, errors }
}

export function validateDelta(delta: ArchitectureDelta): EnvelopeValidation {
  const errors: string[] = []

  if (!delta.request_id) {
    errors.push('request_id is required')
  }
  if (!delta.based_on_baseline) {
    errors.push('based_on_baseline is required — delta must declare which baseline version it was authored against')
  }
  if (typeof delta.理由 !== 'string' || delta.理由.trim() === '') {
    errors.push('理由 is required — explain why this update is needed')
  }
  if (!Array.isArray(delta.冲击不变量) || delta.冲击不变量.length === 0) {
    errors.push('冲击不变量 is required — list impacted invariants (use [] only if explicitly none, but field must list them)')
  }
  if (!Array.isArray(delta.gate_变更)) {
    errors.push('gate_变更 must be an array (may be empty)')
  }
  if (!UPGRADE_FLAGS.includes(delta.升级标记)) {
    errors.push(`升级标记 must be one of: ${UPGRADE_FLAGS.join(', ')}`)
  }
  if (!delta.delta || typeof delta.delta !== 'object') {
    errors.push('delta field is required — must contain the overlay on baseline')
  }

  return { valid: errors.length === 0, errors }
}

export function mergeBaselineDelta(baseline: ArchitectureBaseline, delta: ArchitectureDelta): ArchitectureBaseline {
  const d = delta.delta
  const mergedWritable = d.module_boundaries?.writable
    ? [...baseline.module_boundaries.writable, ...d.module_boundaries.writable]
    : baseline.module_boundaries.writable
  const mergedReadOnly = d.module_boundaries?.read_only
    ? [...baseline.module_boundaries.read_only, ...d.module_boundaries.read_only]
    : baseline.module_boundaries.read_only
  const mergedForbidden = d.module_boundaries?.forbidden
    ? [...baseline.module_boundaries.forbidden, ...d.module_boundaries.forbidden]
    : baseline.module_boundaries.forbidden

  const mergedInterfaces = d.declared_interfaces
    ? mergeDeclaredInterfaces(baseline.declared_interfaces, d.declared_interfaces)
    : baseline.declared_interfaces

  return {
    version: baseline.version,
    frozen_at: baseline.frozen_at,
    frozen_at_sha: baseline.frozen_at_sha,
    frozen_by_req: baseline.frozen_by_req,
    legal_layer: d.legal_layer ?? baseline.legal_layer,
    module_boundaries: {
      writable: mergedWritable,
      read_only: mergedReadOnly,
      forbidden: mergedForbidden,
    },
    declared_interfaces: mergedInterfaces,
    absorption_budget: d.absorption_budget ?? baseline.absorption_budget,
  }
}

export function deriveEnvelope(
  baseline: ArchitectureBaseline,
  delta?: ArchitectureDelta,
): ArchitectureEnvelope {
  const merged = delta ? mergeBaselineDelta(baseline, delta) : baseline
  return {
    version: merged.version,
    request_id: delta?.request_id ?? merged.frozen_by_req,
    legal_layer: merged.legal_layer,
    module_boundaries: merged.module_boundaries,
    absorption_budget: merged.absorption_budget,
  }
}

export function seedBaselineFromEnvelope(
  envelope: ArchitectureEnvelope,
  reqId: string,
): ArchitectureBaseline {
  return {
    version: envelope.version,
    frozen_at: '',
    frozen_by_req: reqId,
    legal_layer: envelope.legal_layer,
    module_boundaries: envelope.module_boundaries,
    declared_interfaces: [],
    absorption_budget: envelope.absorption_budget,
  }
}

export function freezeBaseline(
  baseline: ArchitectureBaseline,
  delta: ArchitectureDelta,
  frozenAt: string,
  frozenAtSha?: string,
): ArchitectureBaseline {
  const merged = mergeBaselineDelta(baseline, delta)
  return {
    ...merged,
    frozen_at: frozenAt,
    frozen_at_sha: frozenAtSha ?? merged.frozen_at_sha,
    frozen_by_req: delta.request_id,
  }
}

function mergeDeclaredInterfaces(
  existing: DeclaredInterface[],
  additions: DeclaredInterface[],
): DeclaredInterface[] {
  const byModule = new Map<string, DeclaredInterface>()
  for (const i of existing) {
    byModule.set(i.module, { ...i, exports: [...i.exports] })
  }
  for (const add of additions) {
    const cur = byModule.get(add.module)
    if (cur) {
      const set = new Set([...cur.exports, ...add.exports])
      cur.exports = [...set]
      cur.source_glob = add.source_glob || cur.source_glob
    } else {
      byModule.set(add.module, { ...add, exports: [...add.exports] })
    }
  }
  return [...byModule.values()]
}

function normalizeBaseline(raw: any): ArchitectureBaseline {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Baseline YAML is empty or not an object')
  }
  if (!raw.module_boundaries) {
    throw new Error('Baseline missing required field: module_boundaries')
  }
  const defaultLevel: ChangeLevel = 'L0'
  return {
    version: raw.version ?? '1.0',
    frozen_at: raw.frozen_at ?? '',
    frozen_at_sha: typeof raw.frozen_at_sha === 'string' && raw.frozen_at_sha.trim()
      ? raw.frozen_at_sha.trim()
      : undefined,
    frozen_by_req: raw.frozen_by_req ?? '',
    legal_layer: raw.legal_layer,
    module_boundaries: {
      writable: (raw.module_boundaries.writable ?? []).map((w: any) => ({
        module: w.module,
        paths: w.paths ?? [],
        change_level: (w.change_level ?? defaultLevel) as ChangeLevel,
      })),
      read_only: (raw.module_boundaries.read_only ?? []).map((r: any) => ({
        module: r.module,
        paths: r.paths ?? [],
      })),
      forbidden: (raw.module_boundaries.forbidden ?? []).map((f: any) => ({
        module: f.module,
        paths: f.paths ?? [],
      })),
    },
    declared_interfaces: (raw.declared_interfaces ?? []).map((i: any) => ({
      module: i.module,
      source_glob: i.source_glob ?? '',
      exports: i.exports ?? [],
    })),
    absorption_budget: {
      max_parallel_slices: raw.absorption_budget?.max_parallel_slices ?? 1,
    },
  }
}

function normalizeDelta(raw: any): ArchitectureDelta {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Delta YAML is empty or not an object')
  }
  const d = raw.delta ?? {}
  return {
    request_id: raw.request_id ?? '',
    based_on_baseline: raw.based_on_baseline ?? '',
    delta: {
      legal_layer: d.legal_layer,
      module_boundaries: d.module_boundaries
        ? {
            writable: (d.module_boundaries.writable ?? []).map((w: any) => ({
              module: w.module,
              paths: w.paths ?? [],
              change_level: (w.change_level ?? 'L0') as ChangeLevel,
            })),
            read_only: (d.module_boundaries.read_only ?? []).map((r: any) => ({
              module: r.module,
              paths: r.paths ?? [],
            })),
            forbidden: (d.module_boundaries.forbidden ?? []).map((f: any) => ({
              module: f.module,
              paths: f.paths ?? [],
            })),
          }
        : undefined,
      declared_interfaces: (d.declared_interfaces ?? []).map((i: any) => ({
        module: i.module,
        source_glob: i.source_glob ?? '',
        exports: i.exports ?? [],
      })),
      absorption_budget: d.absorption_budget,
    },
    理由: raw['理由'] ?? '',
    冲击不变量: raw['冲击不变量'] ?? [],
    gate_变更: (raw['gate_变更'] ?? []).map((g: any) => ({
      gate_id: g.gate_id ?? '',
      change: g.change ?? '',
      reason: g.reason ?? '',
    })),
    升级标记: (raw['升级标记'] ?? 'none') as UpgradeFlag,
  }
}
